import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

const FALLBACK_IMGS = [
  "https://images.unsplash.com/photo-1637944220604-c5f28faac604?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1674403442429-95df05a192e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
];

export default function RecipeReader() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [unit, setUnit] = useState("metric");
  const [scale, setScale] = useState(1);
  const [image, setImage] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    client.get(`/content/recipes/${slug}`).then(({ data }) => {
      setRecipe(data.recipe);
    }).catch(() => navigate("/read"));
    client.get(`/images/recipe/${slug}`).then(({ data }) => {
      if (data.image_data_url) setImage(data.image_data_url);
    }).catch(() => {});
  }, [slug, navigate]);

  const generateImage = async () => {
    setGenLoading(true);
    try {
      const { data } = await client.post("/images/generate", { recipe_slug: slug });
      setImage(data.image_data_url);
      toast.success("Hero image generated.");
    } catch { toast.error("Image generation failed. Try again."); }
    setGenLoading(false);
  };

  if (!recipe) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl">Loading recipe…</div>;

  const ingredients = unit === "metric" ? recipe.ingredients_metric : recipe.ingredients_us;
  const scaleIngredient = (line) => {
    if (scale === 1) return line;
    return line.replace(/(\d+(?:[.,/]\d+)?)/, (m) => {
      const n = parseFloat(m.replace(",", "."));
      if (isNaN(n)) return m;
      const out = n * scale;
      return Number.isInteger(out) ? String(out) : out.toFixed(1).replace(/\.0$/, "");
    });
  };

  return (
    <article className="bg-mizizi-cream text-mizizi-charcoal">
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <Link to="/read" data-testid="back-reader" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← Table of Contents</Link>
      </div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pb-8 text-center">
        <div className="overline text-mizizi-gold">{recipe.chapter}</div>
        <h1 className="font-serif font-light text-4xl md:text-6xl mt-4 leading-tight">{recipe.title}</h1>
        <p className="mt-6 max-w-2xl mx-auto italic text-mizizi-charcoal/70 text-lg">{recipe.story}</p>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="aspect-[16/10] overflow-hidden bg-mizizi-forest relative">
          <img
            src={image || FALLBACK_IMGS[recipe.slug.length % FALLBACK_IMGS.length]}
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
          {!image && (
            <button
              onClick={generateImage}
              disabled={genLoading}
              data-testid="gen-image"
              className="absolute bottom-6 right-6 pill pill-primary text-xs disabled:opacity-60"
            >
              {genLoading ? "Generating…" : "✨ Generate AI Hero Photo"}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-10 mt-12 grid grid-cols-4 gap-4 text-center hairline-b pb-8" data-testid="recipe-stats">
        <div><div className="overline text-mizizi-gold">Prep</div><div className="font-serif text-2xl md:text-3xl">{recipe.prep_min}′</div></div>
        <div><div className="overline text-mizizi-gold">Cook</div><div className="font-serif text-2xl md:text-3xl">{recipe.cook_min}′</div></div>
        <div><div className="overline text-mizizi-gold">Level</div><div className="font-serif text-xl md:text-2xl">{recipe.difficulty}</div></div>
        <div><div className="overline text-mizizi-gold">Spice</div><div className="font-serif text-xl md:text-2xl">{recipe.spice}</div></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-10 mt-6 flex flex-wrap justify-between items-center gap-6">
        <div className="flex gap-2 flex-wrap">
          {recipe.gf && <span className="overline border border-mizizi-gold/50 px-3 py-1">Gluten-Free</span>}
          {recipe.nut_free && <span className="overline border border-mizizi-gold/50 px-3 py-1">Nut-Free</span>}
          {(recipe.allergens || []).map(a => (
            <span key={a} className="overline border border-mizizi-terracotta/50 text-mizizi-terracotta px-3 py-1">contains {a}</span>
          ))}
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center border border-mizizi-gold/40" data-testid="unit-toggle">
            <button
              onClick={() => setUnit("metric")}
              data-testid="unit-metric"
              className={`px-4 py-2 overline transition-colors ${unit === "metric" ? "bg-mizizi-gold text-mizizi-cream" : "text-mizizi-charcoal"}`}
            >Metric</button>
            <button
              onClick={() => setUnit("us")}
              data-testid="unit-us"
              className={`px-4 py-2 overline transition-colors ${unit === "us" ? "bg-mizizi-gold text-mizizi-cream" : "text-mizizi-charcoal"}`}
            >US</button>
          </div>
          <div className="flex items-center gap-3" data-testid="servings-scaler">
            <span className="overline text-mizizi-charcoal/60">Serves</span>
            <button onClick={() => setScale(Math.max(0.5, scale - 0.5))} data-testid="scale-down" className="w-8 h-8 border border-mizizi-gold/40 hover:bg-mizizi-gold/10">−</button>
            <span className="font-serif text-2xl w-10 text-center">{Math.round(recipe.servings * scale)}</span>
            <button onClick={() => setScale(scale + 0.5)} data-testid="scale-up" className="w-8 h-8 border border-mizizi-gold/40 hover:bg-mizizi-gold/10">+</button>
          </div>
        </div>
      </div>

      <Divider />

      <div className="max-w-4xl mx-auto px-6 md:px-10 grid md:grid-cols-5 gap-12">
        <section className="md:col-span-2" data-testid="ingredients-section">
          <div className="overline text-mizizi-terracotta">Ingredients</div>
          <ul className="mt-6 space-y-3 font-serif text-lg">
            {ingredients.map((line) => (
              <li key={line} className="hairline-b pb-2">{scaleIngredient(line)}</li>
            ))}
          </ul>
        </section>
        <section className="md:col-span-3" data-testid="method-section">
          <div className="overline text-mizizi-terracotta">Method</div>
          <ol className="mt-6 space-y-6">
            {recipe.steps.map((step, i) => (
              <li key={step} className="flex gap-5">
                <div className="font-serif text-3xl text-mizizi-gold w-10 shrink-0">{String(i + 1).padStart(2, "0")}</div>
                <p className="text-lg leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <Divider />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-2 gap-10">
        {[
          ["Substitutions", recipe.substitutions],
          ["Make Ahead", recipe.make_ahead],
          ["Storage", recipe.storage],
          ["Plating", recipe.plating],
          ["Make It Remarkable", recipe.chef_tip],
          ["Pairing", recipe.pairing],
        ].map(([title, body]) => (
          <div key={title} className="border-l-2 border-mizizi-gold/50 pl-5" data-testid={`meta-${title.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="overline text-mizizi-gold">{title}</div>
            <p className="mt-3 text-mizizi-charcoal/80 leading-relaxed">{body}</p>
          </div>
        ))}
      </section>
    </article>
  );
}
